import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useApiToken from "../../hooks/useApiToken";
import { AssessmentSlots, InterviewSlots } from "../../Models";
import BookInterviewSlotModal from "./BookInterviewSlot";
import RescheduleInterviewModal from "./RescheduleInterviewModal";
import ConfirmModal from "../Shared/ConfirmModal";
import { useNotification } from "../../context/NotificationContext";
import { 
  PlusIcon, 
  XMarkIcon, 
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  LinkIcon,
  StarIcon,
  ChevronDownIcon,
  ChevronUpIcon 
} from "@heroicons/react/24/outline";
import {
  GetCandidateAssessmentSlots,
  GetCandidateInterviews,
  UnbookAssessmentSlot,
  GetCandidateInterviewAnswers,
  GetInterviewSlotDetails,
  createInterviewSession,
} from "../../api/candidates.api";
import { UnbookInterviewSlot } from "../../api/roles.api";
import { getAllInterviewAssessments } from "../../api/assessmentquestionnaires.api";
import InterviewControlPanel from "../Shared/Modals/InterviewControlPanel";
import InterviewScorePanel, {
  InterviewSession,
} from "../Shared/Modals/InterviewScorePanel";
import { AssessmentDetailsModel } from "../Assessment/AssessmentQuestions";
import { AssessmentUrl } from "../../config/appConfig";

export interface AssessmentDetailsModelWithId extends AssessmentDetailsModel {
  id: string;
}

interface InterviewsProps {
  interviewAssessments?: AssessmentDetailsModelWithId[];
  selectedAssessment?: AssessmentDetailsModelWithId | null;
  setSelectedAssessment?: (assessment: AssessmentDetailsModelWithId | null) => void;
  isInterviewQuestionModalOpen?: boolean;
  setIsInterviewQuestionModalOpen?: (open: boolean) => void;
  candidateInterviewScoreOpen?: boolean;
  setCandidateInterviewScoreOpen?: (open: boolean) => void;
  activeInterviewId?: string | null;
  setActiveInterviewId?: (id: string | null) => void;
  interviewSession?: InterviewSession | null;
  setInterviewSession?: (session: InterviewSession | null) => void;
  setShowCopyLinkNotification?: (show: boolean) => void;
}

type TabType = "interviews" | "assessments";

export default function Interviews({
  interviewAssessments = [],
  selectedAssessment,
  setSelectedAssessment,
  isInterviewQuestionModalOpen = false,
  setIsInterviewQuestionModalOpen,
  candidateInterviewScoreOpen = false,
  setCandidateInterviewScoreOpen,
  activeInterviewId,
  setActiveInterviewId,
  interviewSession,
  setInterviewSession,
  setShowCopyLinkNotification,
}: InterviewsProps = {}) {
  const [activeTab, setActiveTab] = useState<TabType>("interviews");
  const [interviews, setInterviews] = useState<InterviewSlots[]>([]);
  const [assessments, setAssessments] = useState<AssessmentSlots[]>([]);
  const [interviewAssessments_local, setInterviewAssessments_local] = useState<{ [interviewId: string]: AssessmentDetailsModelWithId | null }>({});
  const [localInterviewAssessments, setLocalInterviewAssessments] = useState<AssessmentDetailsModelWithId[]>([]);
  const [expandedManagement, setExpandedManagement] = useState<{ [interviewId: string]: boolean }>({});
  const { candidateId, roleId } = useParams();
  const token = useApiToken();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isUnbookModalOpen, setIsUnbookModalOpen] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [selectedInterviewForReschedule, setSelectedInterviewForReschedule] = useState<InterviewSlots | null>(null);
  const { success, error: notifyError } = useNotification();

  const fetchInterviewSlotDetails = async () => {
    if (!candidateId || !token || !roleId) {
      return;
    }
    try {
      const interviewSlot = await GetCandidateInterviews(
        candidateId,
        roleId,
        token,
      );
      setInterviews(interviewSlot);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchAssessmentSlotDetails = async () => {
    if (!candidateId || !token) {
      return;
    }
    try {
      const assessmentSlot = await GetCandidateAssessmentSlots(
        candidateId,
        token,
      );
      setAssessments(assessmentSlot || []);
    } catch (error) {
      console.error("Error fetching assessment assessmentSlot:", error);
    }
  };

  useEffect(() => {
    fetchInterviewSlotDetails();
  }, [candidateId, token, roleId]);

  useEffect(() => {
    fetchAssessmentSlotDetails();
  }, [candidateId, token, roleId]);

  useEffect(() => {
    getLocalInterviewAssessments();
  }, [token]);

  // Set default selected assessment based on interview's assessmentQuestionnaireId
  useEffect(() => {
    if (interviews && interviews.length > 0 && localInterviewAssessments.length > 0) {
      const newInterviewAssessments: { [interviewId: string]: AssessmentDetailsModelWithId | null } = {};
      
      interviews.forEach((interview: any) => {
        if (interview.assessmentQuestionnaireId) {
          const matchingAssessment = localInterviewAssessments.find(
            (a) => a.id === interview.assessmentQuestionnaireId
          );
          if (matchingAssessment) {
            newInterviewAssessments[interview.id] = matchingAssessment;
          }
        }
      });
      
      if (Object.keys(newInterviewAssessments).length > 0) {
        setInterviewAssessments_local(prev => ({
          ...prev,
          ...newInterviewAssessments
        }));
      }
    }
  }, [interviews, localInterviewAssessments])

  const getLocalInterviewAssessments = async () => {
    if (!token) return;
    try {
      const res = await getAllInterviewAssessments(token);
      setLocalInterviewAssessments(res || []);
    } catch (error) {
      console.log(error);
    }
  };

  const getCandidateInterviewAnswers = async (interviewId: string) => {
    if (!candidateId || !interviewId) return;
    const answers = await GetCandidateInterviewAnswers(
      candidateId,
      interviewId,
    );
    if (setInterviewSession) {
      setInterviewSession(answers || null);
    }
  };

  const getInterviewIdForSlot = async (interview: InterviewSlots): Promise<string | null> => {
    if (!candidateId || !roleId) return null;
    return interview.id
  };

  const handleUnbookSlot = async () => {
    if (!selectedSlotId || !token) return;

    const interviewSlot = interviews?.find((i) => i.id === selectedSlotId);
    const assessmentSlot = assessments?.find((a) => a.id === selectedSlotId);

    let unbookFn: ((id: string, token: string) => Promise<any>) | null = null;
    let slotType = "";

    if (interviewSlot) {
      unbookFn = UnbookInterviewSlot;
      slotType = "Interview slot";
    } else if (assessmentSlot) {
      unbookFn = UnbookAssessmentSlot;
      slotType = "Assessment slot";
    }

    if (!unbookFn) {
      notifyError("Error", "Could not determine slot type to unbook.");
      setIsUnbookModalOpen(false);
      setSelectedSlotId(null);
      return;
    }

    try {
      await unbookFn(selectedSlotId, token);
      success("Success", `${slotType} unbooked successfully`);
      fetchInterviewSlotDetails();
      fetchAssessmentSlotDetails();
    } catch (error: any) {
      notifyError("Error", error.message || "Failed to unbook slot");
    } finally {
      setIsUnbookModalOpen(false);
      setSelectedSlotId(null);
    }
  };

  return (
    <>
      <div
        className={
          "py-3 px-4 bg-white rounded-xl shadow-sm z-[11] border border-gray-100 h-fit dark:bg-darkbglight dark:border-gray-500 mt-4"
        }
      >
        <div className="flex justify-between items-center mb-3">
          <h2
            id="timeline-title"
            className="text-lg font-medium text-gray-900 dark:text-gray-300"
          >
            Events
          </h2>
          <button
            onClick={() => setIsBookingModalOpen(true)}
            className="inline-flex items-center rounded-md bg-entntblue px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-entntorange"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Book Slot
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-600 mb-4">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("interviews")}
              className={`${
                activeTab === "interviews"
                  ? "border-entntblue text-entntblue"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              Interviews
            </button>
            <button
              onClick={() => setActiveTab("assessments")}
              className={`${
                activeTab === "assessments"
                  ? "border-entntblue text-entntblue"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              Assessments
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "interviews" ? (
          <div id="interviewsEvents" className="overflow-y-auto max-h-[375px] pr-1">
            {interviews && interviews.length > 0 ? (
              interviews.map((interview) => (
                <div
                  className="p-6 bg-blue-50 dark:bg-slate-800 rounded-lg mb-4 space-y-3 border border-blue-200 dark:border-slate-600 shadow-sm relative"
                  key={interview.id}
                >
                  {/* Action Buttons */}
                  <div className="absolute top-3 right-3 flex items-center gap-2">
                    {/* Reschedule button */}
                    <button
                      onClick={() => {
                        setSelectedInterviewForReschedule(interview);
                        setIsRescheduleModalOpen(true);
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors group"
                      title="Reschedule interview"
                    >
                      <CalendarDaysIcon className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400" />
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Reschedule</span>
                    </button>

                    {/* Unbook button */}
                    <button
                      onClick={() => {
                        setSelectedSlotId(interview.id);
                        setIsUnbookModalOpen(true);
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900 transition-colors group"
                      title="Unbook interview"
                    >
                      <XMarkIcon className="h-3.5 w-3.5 text-red-500 dark:text-red-400 group-hover:text-red-600 dark:group-hover:text-red-300" />
                      <span className="text-xs font-medium text-red-600 dark:text-red-400 group-hover:text-red-700 dark:group-hover:text-red-300">Unbook</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(interview.startTime).toLocaleDateString(
                          undefined,
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            weekday: "short",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(interview.endTime).toLocaleDateString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                          weekday: "short",
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">Interviewers</span>
                      <div className="space-y-1">
                        {interview.interviewerEmail
                          ?.split(",")
                          .map((email: string, idx: number) => (
                            <div key={idx} className="inline-flex items-center bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-md mr-2 mb-1">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{email.trim()}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Stage: </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                        {interview.roleStatus.name}
                      </span>
                    </div>
                  </div>

                  {interview.meetingLink && (
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">Meeting Link</span>
                      <a
                        href={interview.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-700 dark:text-green-300 rounded-md text-sm font-medium transition-colors"
                      >
                        <LinkIcon className="h-4 w-4" />
                        Join Meeting
                      </a>
                    </div>
                  )}

                  {/* Interview Management Controls */}
                  <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => {
                        setExpandedManagement(prev => ({
                          ...prev,
                          [interview.id]: !prev[interview.id]
                        }));
                      }}
                      className="flex items-center justify-between w-full text-left hover:bg-gray-50 dark:hover:bg-slate-700/50 rounded-lg p-3 transition-colors"
                    >
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Interview Management
                      </h4>
                      {expandedManagement[interview.id] ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    
                    {expandedManagement[interview.id] && (
                      <div className="mt-4 pt-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                        {/* Assessment Selection for this interview */}
                        <div className="">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Select Assessment
                          </label>
                          <div className="relative">
                            <select
                              value={interviewAssessments_local[interview.id]?.id || ""}
                              onChange={async (e) => {
                                const selectedId = e.target.value;
                                const selectedAssessment = localInterviewAssessments.find(a => a.id === selectedId) || null;
                                setInterviewAssessments_local(prev => ({
                                  ...prev,
                                  [interview.id]: selectedAssessment
                                }));
                                
                                // Create interview session when assessment is selected
                                if (selectedId && roleId && token) {
                                  try {
                                    await createInterviewSession(selectedId, candidateId || "", roleId, interview.id, token);
                                    success("Success", "Assessment linked to interview successfully");
                                  } catch (error: any) {
                                    notifyError("Error", error.message || "Failed to link assessment to interview");
                                  }
                                }
                              }}
                              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 appearance-none pr-12"
                            >
                              <option value="">No assessment selected</option>
                              {localInterviewAssessments.map((assessment) => (
                                <option key={assessment.id} value={assessment.id}>
                                  {assessment.title || 'Unnamed Assessment'}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        {interviewAssessments_local[interview.id]?.id && <div className="flex flex-col gap-4 p-3">
                          <button
                            onClick={async () => {
                              const interviewId = await getInterviewIdForSlot(interview);
                              if (interviewId && setActiveInterviewId && setIsInterviewQuestionModalOpen) {
                                setActiveInterviewId(interviewId);
                                // Set the selected assessment for this specific interview
                                if (setSelectedAssessment && interviewAssessments_local[interview.id]) {
                                  setSelectedAssessment(interviewAssessments_local[interview.id]);
                                }
                                setIsInterviewQuestionModalOpen(true);
                              }
                            }}
                            className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-3 text-sm font-medium text-white shadow-sm hover:shadow-md transition-all duration-200 min-h-[44px]"
                          >
                            <ClipboardDocumentListIcon className="h-4 w-4" />
                            Set Questions
                          </button>
                          <button
                            className="flex items-center justify-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 px-4 py-3 text-sm font-medium text-white shadow-sm hover:shadow-md transition-all duration-200 min-h-[44px]"
                            onClick={async () => {
                              const interviewId = await getInterviewIdForSlot(interview);
                              if (interviewId) {
                                const interviewUrl = `${AssessmentUrl}/interview/${interviewId}`;

                              

                                navigator.clipboard.writeText(interviewUrl);
                                if (setShowCopyLinkNotification) {
                                  setShowCopyLinkNotification(true);
                                }
                              }
                            }}
                          >
                            <LinkIcon className="h-4 w-4" />
                            Copy Link
                          </button>
                          <button
                            className="flex items-center justify-center gap-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 px-4 py-3 text-sm font-medium text-white shadow-sm hover:shadow-md transition-all duration-200 min-h-[44px]"
                            onClick={async () => {
                              const interviewId = await getInterviewIdForSlot(interview);
                              if (interviewId && setActiveInterviewId && setCandidateInterviewScoreOpen) {
                                setActiveInterviewId(interviewId);
                                await getCandidateInterviewAnswers(interviewId);
                                setCandidateInterviewScoreOpen(true);
                              }
                            }}
                          >
                            <StarIcon className="h-4 w-4" />
                            View Score
                          </button>
                        </div>}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                No interview slots booked yet.
              </p>
            )}
          </div>
        ) : (
          <div id="assessmentsEvents">
            {assessments && assessments.length > 0 ? (
              assessments.map((assessment) => (
                <div
                  className="p-6 bg-blue-50 dark:bg-slate-800 rounded-lg mb-4 space-y-3 border border-blue-200 dark:border-slate-600 shadow-sm relative"
                  key={assessment.id}
                >
                  <button
                    onClick={() => {
                      setSelectedSlotId(assessment.id);
                      setIsUnbookModalOpen(true);
                    }}
                    className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900 transition-colors group"
                    title="Unbook assessment"
                  >
                    <XMarkIcon className="h-4 w-4 text-red-500 dark:text-red-400 group-hover:text-red-600 dark:group-hover:text-red-300" />
                  </button>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(assessment.startTime).toLocaleDateString(
                          undefined,
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            weekday: "short",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(assessment.endTime).toLocaleDateString(
                          undefined,
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            weekday: "short",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Stage: </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                      {assessment.roleStatus?.name}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                No assessments booked yet.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {isInterviewQuestionModalOpen && activeInterviewId && candidateId && roleId && activeInterviewId && (
        <InterviewControlPanel
          interviewId={activeInterviewId}
          candidateId={candidateId}
          roleId={roleId}
          onClose={() => setIsInterviewQuestionModalOpen && setIsInterviewQuestionModalOpen(false)}
        />
      )}
      
      {candidateInterviewScoreOpen && activeInterviewId && (
        <InterviewScorePanel
          interviewSession={interviewSession || null}
          onClose={() => setCandidateInterviewScoreOpen && setCandidateInterviewScoreOpen(false)}
        />
      )}

      <BookInterviewSlotModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        candidateId={candidateId || ""}
        roleId={roleId || ""}
        onSlotBooked={() => {
          fetchInterviewSlotDetails();
          fetchAssessmentSlotDetails();
        }}
      />

      <ConfirmModal
        type="danger"
        open={isUnbookModalOpen}
        setOpen={setIsUnbookModalOpen}
        onConfirm={handleUnbookSlot}
        title="Unbook Event"
        description="Are you sure you want to unbook this event? The candidate will receive a cancellation email."
      />

      {isRescheduleModalOpen && selectedInterviewForReschedule && (
        <RescheduleInterviewModal
          isOpen={isRescheduleModalOpen}
          onClose={() => {
            setIsRescheduleModalOpen(false);
            setSelectedInterviewForReschedule(null);
          }}
          interview={selectedInterviewForReschedule}
          roleId={roleId || ""}
          candidateId={candidateId || ""}
          onRescheduled={fetchInterviewSlotDetails}
        />
      )}
    </>
  );
}
